import { useCallback, useEffect, useState } from "react";
import {
  getTrainingScenarioImageMetadata,
  type TrainingImageMetadataView,
} from "@/api/trainingEval";
import type { LoadState } from "@/components/dataset";
import { createLogger } from "@/logging";
import { isHttpClientError } from "@/http";
import { resolveEvalLoadErrorMessage } from "@/modules/evaluation/public";

export type EvalImageMetadataState = LoadState<TrainingImageMetadataView | null>;
export type EvalImageMetadataStates = Readonly<Record<string, EvalImageMetadataState>>;

const logger = createLogger("page/evaluationResults");

const MISSING: EvalImageMetadataState = { status: "success", data: null };
const LOADING: EvalImageMetadataState = { status: "loading" };

export function imageMetadataState(
  states: EvalImageMetadataStates,
  tag: string | null,
): EvalImageMetadataState {
  return tag === null ? MISSING : (states[tag] ?? LOADING);
}

// 응답 캐시는 API 계층이 소유한다. 이 hook 은 표시 중인 tag 의 조회 상태만 관리한다.
export function useEvalImageMetadata(tags: readonly string[], enabled: boolean) {
  const [states, setStates] = useState<EvalImageMetadataStates>({});
  const [revision, setRevision] = useState(0);
  const tagKey = JSON.stringify([...new Set(tags)].sort());
  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    const selectedTags: string[] = JSON.parse(tagKey);
    setStates((previous) =>
      Object.fromEntries(
        selectedTags.map((tag) => [
          tag,
          previous[tag]?.status === "success" && previous[tag].data !== null
            ? previous[tag]
            : LOADING,
        ]),
      ),
    );
    for (const tag of selectedTags) {
      void getTrainingScenarioImageMetadata(tag)
        .then((data) => {
          if (!disposed)
            setStates((previous) => ({ ...previous, [tag]: { status: "success", data } }));
        })
        .catch((error: unknown) => {
          if (disposed) return;
          const state: EvalImageMetadataState =
            isHttpClientError(error) && error.status === 404
              ? MISSING
              : { status: "error", message: resolveEvalLoadErrorMessage(error) };
          if (state.status === "error")
            logger.error("getTrainingScenarioImageMetadata failed", error);
          setStates((previous) => ({ ...previous, [tag]: state }));
        });
    }
    return () => {
      disposed = true;
    };
  }, [enabled, tagKey, revision]);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  return { states, retry };
}
